import * as ToastPrimitive from "@radix-ui/react-toast";
import { AlertTriangle, Bell, Check, X } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Toasts, kept deliberately small: this console confirms writes inline wherever it can, and a
 * toast is for the case where the thing that changed is no longer on screen (a deleted row, a
 * rotated password). Radix handles the swipe-to-dismiss and the screen-reader live region.
 *
 * A notice (`notify`, ACL-506) is the one toast about something nobody just did, as a reminder of an
 * appointment or a callback is: it stays on screen longer, has a bell for its mark, and carries the
 * one thing to do about it, as a button.
 */

type Tone = "success" | "error" | "warning" | "info" | "notice";

/** A toast's one button: "Open", for a notice. Pressing it closes the toast too. */
export interface ToastAction {
  label: string;
  onSelect: () => void;
}

interface ToastMessage {
  id: number;
  title: string;
  description?: string;
  tone: Tone;
  action?: ToastAction;
}

interface ToastApi {
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  /** What was done, short of what was asked: "The callback is set", without its backup time. */
  warning: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
  /** Something nobody just did (a reminder): longer on screen, with a bell and a button. */
  notify: (title: string, description?: string, action?: ToastAction) => void;
}

/** How long a notice stays: long enough to be seen by a rep who looked away for a moment. */
const NOTICE_MS = 15_000;

const ToastContext = React.createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = React.useState<ToastMessage[]>([]);
  const nextId = React.useRef(0);

  const push = React.useCallback(
    (tone: Tone, title: string, description?: string, action?: ToastAction) => {
      setMessages((prev) => [...prev, { id: nextId.current++, title, description, tone, action }]);
    },
    [],
  );

  const api = React.useMemo<ToastApi>(
    () => ({
      success: (t, d) => push("success", t, d),
      error: (t, d) => push("error", t, d),
      warning: (t, d) => push("warning", t, d),
      info: (t, d) => push("info", t, d),
      notify: (t, d, a) => push("notice", t, d, a),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      <ToastPrimitive.Provider swipeDirection="right" duration={5000}>
        {children}
        {messages.map((m) => (
          <ToastPrimitive.Root
            key={m.id}
            duration={m.tone === "notice" ? NOTICE_MS : undefined}
            onOpenChange={(open) =>
              !open && setMessages((prev) => prev.filter((x) => x.id !== m.id))
            }
            className={cn(
              "flex items-start gap-2.5 rounded-lg border bg-surface-raised p-3 shadow-lg",
              "data-[state=open]:animate-in data-[state=open]:slide-in-from-right-full",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out-80",
              "data-[swipe=end]:animate-out data-[swipe=end]:fade-out-0",
              m.tone === "error" ? "border-danger-4" : m.tone === "warning" ? "border-warning-4" : "border-border",
            )}
          >
            <span
              className={cn(
                "mt-px flex size-4 shrink-0 items-center justify-center rounded-full",
                m.tone === "success" && "bg-success-9 text-white",
                m.tone === "error" && "bg-danger-9 text-white",
                m.tone === "warning" && "bg-warning-9 text-white",
                (m.tone === "info" || m.tone === "notice") && "bg-accent-9 text-white",
              )}
              aria-hidden
            >
              {m.tone === "error" || m.tone === "warning" ? (
                <AlertTriangle className="size-2.5" />
              ) : m.tone === "notice" ? (
                <Bell className="size-2.5" />
              ) : (
                <Check className="size-2.5" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <ToastPrimitive.Title className="text-sm font-medium text-text">
                {m.title}
              </ToastPrimitive.Title>
              {m.description && (
                <ToastPrimitive.Description className="mt-0.5 text-xs text-text-muted">
                  {m.description}
                </ToastPrimitive.Description>
              )}
              {m.action && (
                <ToastPrimitive.Action
                  altText={m.action.label}
                  onClick={m.action.onSelect}
                  className="mt-2 rounded-md border border-border px-2 py-1 text-xs font-medium text-text hover:bg-surface-hover"
                >
                  {m.action.label}
                </ToastPrimitive.Action>
              )}
            </div>
            <ToastPrimitive.Close className="rounded p-0.5 text-text-subtle hover:bg-surface-hover hover:text-text">
              <X className="size-3.5" />
              <span className="sr-only">Dismiss</span>
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport
          className={cn(
            "fixed bottom-0 right-0 z-100 flex max-h-screen w-full flex-col-reverse gap-2",
            "p-4 sm:max-w-sm",
          )}
        />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
